import { Lazy } from '../chat/lazy';

export type WorkflowTriggerKind = 'cron' | 'webhook' | 'event' | 'dataset';

export type WorkflowTriggerBaseData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly enabled: boolean;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

/**
 * Base class for persisted workflow triggers. Use {@link isCronTrigger} /
 * {@link isWebhookTrigger} / {@link isEventTrigger} / {@link isDatasetTrigger}
 * to narrow to concrete subclasses.
 */
export abstract class WorkflowTrigger<
  TData extends WorkflowTriggerBaseData = WorkflowTriggerBaseData
> {
  protected readonly _data: TData;
  private readonly workflowList: Lazy<readonly import('./workflow').Workflow[]>;

  protected constructor(data: TData, hydration: WorkflowTriggerHydration = {}) {
    this._data = data;
    this.workflowList = new Lazy(
      hydration.loadWorkflows ?? resolveEmptyTriggerWorkflows,
      hydration.preloadedWorkflows
    );
  }

  abstract readonly kind: WorkflowTriggerKind;

  get id(): string {
    return this._data.id;
  }

  get ownerId(): string {
    return this._data.ownerId;
  }

  get ownerOrgId(): string {
    return this._data.ownerOrgId;
  }

  get enabled(): boolean {
    return this._data.enabled;
  }

  get createdAt(): Date {
    return this._data.createdAt;
  }

  get updatedAt(): Date {
    return this._data.updatedAt;
  }

  isCronTrigger(): this is import('./workflow-cron-trigger').WorkflowCronTrigger {
    return this.kind === 'cron';
  }

  isWebhookTrigger(): this is import('./workflow-webhook-trigger').WorkflowWebhookTrigger {
    return this.kind === 'webhook';
  }

  isEventTrigger(): this is import('./workflow-event-trigger').WorkflowEventTrigger {
    return this.kind === 'event';
  }

  isDatasetTrigger(): this is import('./workflow-dataset-trigger').WorkflowDatasetTrigger {
    return this.kind === 'dataset';
  }

  async workflows(): Promise<readonly import('./workflow').Workflow[]> {
    return this.workflowList.get();
  }
}

export type WorkflowTriggerHydration = {
  readonly loadWorkflows?: () => Promise<
    readonly import('./workflow').Workflow[]
  >;
  readonly preloadedWorkflows?: readonly import('./workflow').Workflow[];
};

export async function resolveEmptyTriggerWorkflows(): Promise<
  readonly import('./workflow').Workflow[]
> {
  return [];
}
